import React, { useEffect, useMemo, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { PERMISSIONS } from '../lib/constants';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Search, Plus, Pencil, Trash2, Loader2, FileSpreadsheet, FileDown, X, Pencil as PencilIcon, CheckCircle2, XCircle } from 'lucide-react';
import { formatIDR } from '../lib/format';
import { toast } from 'sonner';
import { ProductSelectorDialog } from '../components/ProductSelectorDialog';
import { NumberInput } from '../components/NumberInput';
import { BulkActionBar, BulkButton } from '../components/BulkActionBar';
import { BulkEditDialog } from '../components/BulkEditDialog';

const CATS = ['Wedding', 'Corporate Event', 'Concert', 'Church Event', 'Government Event', 'Party', 'LED Screen Package', 'Lighting Package', 'Rigging Package', 'Custom'];

const empty = {
  package_name: '', category: 'Custom', description: '',
  package_price: 0, price_overridden: false, is_active: true, notes: '', items: [],
};

export default function Packages() {
  const { role } = useAuth();
  const canEdit = PERMISSIONS.canEdit(role);
  const canDelete = PERMISSIONS.canDeleteFinancial(role);
  const canExport = PERMISSIONS.canImportExport(role);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [showActive, setShowActive] = useState('all');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [productPickOpen, setProductPickOpen] = useState(false);

  // Bulk selection state — Set of package IDs
  const [selected, setSelected] = useState(() => new Set());
  const [bulkEditOpen, setBulkEditOpen] = useState(false);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (search) params.search = search;
      if (category !== 'all') params.category = category;
      if (showActive !== 'all') params.is_active = showActive === 'active';
      const { data } = await api.get('/packages', { params });
      setList(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [search, category, showActive]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      package_name: p.package_name || '',
      category: p.category || 'Custom',
      description: p.description || '',
      package_price: p.package_price || 0,
      price_overridden: p.price_overridden || false,
      is_active: p.is_active !== false,
      notes: p.notes || '',
      items: (p.items || []).map(it => ({ ...it })),
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.package_name.trim()) { toast.error('Nama paket wajib'); return; }
    setSaving(true);
    try {
      const payload = {
        package_name: form.package_name,
        category: form.category,
        description: form.description,
        package_price: form.price_overridden ? Number(form.package_price) || 0 : null,
        price_overridden: !!form.price_overridden,
        is_active: !!form.is_active,
        notes: form.notes,
        items: form.items.map(it => ({
          product_id: it.product_id || null,
          item_name: it.item_name || '',
          description: it.description || '',
          quantity: Number(it.quantity) || 0,
          unit: it.unit || '',
          unit_price: Number(it.unit_price) || 0,
          discount: Number(it.discount) || 0,
        })),
      };
      if (editing) {
        await api.put(`/packages/${editing.id}`, payload);
        toast.success('Paket diperbarui');
      } else {
        await api.post('/packages', payload);
        toast.success('Paket ditambahkan');
      }
      setOpen(false);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
    finally { setSaving(false); }
  };

  const remove = async (p) => {
    try { await api.delete(`/packages/${p.id}`); toast.success('Paket dihapus'); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };
  const toggleActive = async (p) => {
    try { await api.post(`/packages/${p.id}/toggle-active`); load(); }
    catch (err) { toast.error(err?.response?.data?.detail || 'Gagal'); }
  };

  // Add products to package items
  const onProductsPicked = (products) => {
    const newItems = products.map(p => ({
      id: `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      product_id: p.id,
      item_name: p.product_name,
      description: p.description || '',
      quantity: 1,
      unit: p.unit || 'Unit',
      unit_price: p.default_price || 0,
      discount: 0,
    }));
    setForm(f => ({ ...f, items: [...f.items, ...newItems] }));
  };

  const computedTotal = form.items.reduce((acc, it) => acc + Math.max(0, (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) - (parseFloat(it.discount) || 0)), 0);

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: 'blob' });
      const link = URL.createObjectURL(res.data);
      const a = document.createElement('a'); a.href = link; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(link);
    } catch (e) { toast.error('Gagal mengunduh'); }
  };

  // ---- Bulk selection helpers ----
  const visibleIds = useMemo(() => list.map(p => p.id), [list]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every(id => selected.has(id));
  const someVisibleSelected = visibleIds.some(id => selected.has(id));

  const toggleOne = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAllVisible = () => {
    setSelected(prev => {
      const next = new Set(prev);
      if (allVisibleSelected) visibleIds.forEach(id => next.delete(id));
      else visibleIds.forEach(id => next.add(id));
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  const runBulk = async (action, payload = null) => {
    if (selected.size === 0) return;
    const ids = Array.from(selected);
    setBulkBusy(true);
    try {
      const { data } = await api.post('/packages/bulk', { action, ids, payload });
      const label = action === 'delete' ? 'dihapus' : action === 'set_active'
        ? (payload?.value ? 'diaktifkan' : 'dinonaktifkan')
        : 'diperbarui';
      toast.success(`${data.affected} paket ${label}`);
      clearSelection();
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Gagal melakukan aksi massal');
    } finally { setBulkBusy(false); }
  };

  const exportSelected = () => {
    if (selected.size === 0) return;
    const idsParam = Array.from(selected).join(',');
    downloadFile(`/export/packages?ids=${encodeURIComponent(idsParam)}`, 'paket-terpilih.xlsx');
  };

  return (
    <div className="space-y-4" data-testid="packages-page">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input data-testid="packages-search-input" placeholder="Cari nama paket…" className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua Kategori</SelectItem>
            {CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={showActive} onValueChange={setShowActive}>
          <SelectTrigger className="w-[140px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Semua</SelectItem>
            <SelectItem value="active">Aktif</SelectItem>
            <SelectItem value="inactive">Nonaktif</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex-1" />
        {canExport && <Button variant="secondary" onClick={() => downloadFile('/export/packages', 'paket.xlsx')} data-testid="packages-export"><FileSpreadsheet className="h-4 w-4 mr-2" />Export</Button>}
        {canExport && <Button variant="secondary" onClick={() => downloadFile('/import/templates/packages', 'template-paket.xlsx')} data-testid="packages-template"><FileDown className="h-4 w-4 mr-2" />Template</Button>}
        {canEdit && <Button onClick={openNew} data-testid="packages-add-button"><Plus className="h-4 w-4 mr-2" />Tambah Paket</Button>}
      </div>

      {/* Bulk Action Bar + Select All */}
      <div className="space-y-2">
        {list.length > 0 && canEdit && (
          <div className="flex items-center gap-2 text-sm">
            <button
              type="button"
              onClick={toggleAllVisible}
              className="inline-flex items-center gap-1.5 text-muted-foreground hover:text-foreground transition-colors"
              data-testid="packages-select-all"
            >
              <Checkbox
                checked={allVisibleSelected ? true : (someVisibleSelected ? 'indeterminate' : false)}
                onCheckedChange={toggleAllVisible}
                aria-label="Pilih semua paket terlihat"
              />
              <span className="ml-1.5">
                {allVisibleSelected ? `Batal pilih semua (${list.length})` : `Pilih semua (${list.length})`}
              </span>
            </button>
          </div>
        )}

        <BulkActionBar
          count={selected.size}
          onClear={clearSelection}
          itemLabel="paket"
          testid="packages-bulk-bar"
        >
          {canEdit && (
            <BulkButton onClick={() => setBulkEditOpen(true)} data-testid="packages-bulk-edit" disabled={bulkBusy}>
              <PencilIcon className="h-4 w-4 mr-1.5" />Edit Massal
            </BulkButton>
          )}
          {canEdit && (
            <BulkButton onClick={() => runBulk('set_active', { value: true })} data-testid="packages-bulk-activate" disabled={bulkBusy}>
              <CheckCircle2 className="h-4 w-4 mr-1.5" />Aktifkan
            </BulkButton>
          )}
          {canEdit && (
            <BulkButton onClick={() => runBulk('set_active', { value: false })} data-testid="packages-bulk-deactivate" disabled={bulkBusy}>
              <XCircle className="h-4 w-4 mr-1.5" />Nonaktifkan
            </BulkButton>
          )}
          {canExport && (
            <BulkButton onClick={exportSelected} data-testid="packages-bulk-export" disabled={bulkBusy}>
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />Export
            </BulkButton>
          )}
          {canDelete && (
            <BulkButton
              onClick={() => setBulkDeleteOpen(true)}
              className="text-red-300 hover:text-red-200"
              data-testid="packages-bulk-delete"
              disabled={bulkBusy}
            >
              <Trash2 className="h-4 w-4 mr-1.5" />Hapus
            </BulkButton>
          )}
        </BulkActionBar>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {loading && <div className="col-span-full text-center py-8 text-muted-foreground"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Memuat…</div>}
        {!loading && list.length === 0 && <div className="col-span-full text-center py-12 text-muted-foreground">Belum ada paket. Klik “Tambah Paket”.</div>}
        {list.map(p => (
          <Card
            key={p.id}
            className={`bg-card border-border hover:border-white/20 transition-colors ${selected.has(p.id) ? 'ring-1 ring-primary/50 border-primary/40' : ''}`}
            data-testid={`package-card-${p.id}`}
          >
            <CardContent className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  {canEdit && (
                    <Checkbox
                      checked={selected.has(p.id)}
                      onCheckedChange={() => toggleOne(p.id)}
                      aria-label={`Pilih ${p.package_name}`}
                      data-testid={`packages-select-${p.id}`}
                      className="mt-1"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-heading font-semibold text-base truncate">{p.package_name}</div>
                    <div className="text-xs text-muted-foreground">{p.category}</div>
                  </div>
                </div>
                <button onClick={() => canEdit && toggleActive(p)} disabled={!canEdit} className={`text-xs px-2 py-0.5 rounded-full border ${p.is_active ? 'bg-emerald-400/15 text-emerald-300 border-emerald-400/30' : 'bg-zinc-500/15 text-zinc-300 border-zinc-500/30'}`}>
                  {p.is_active ? 'Aktif' : 'Nonaktif'}
                </button>
              </div>
              {p.description && <p className="text-xs text-muted-foreground line-clamp-2">{p.description}</p>}
              <div className="text-xs text-muted-foreground">{p.items?.length || 0} item</div>
              <div className="flex items-end justify-between pt-2 border-t border-border">
                <div>
                  <div className="text-xs text-muted-foreground">Harga Paket</div>
                  <div className="font-heading font-semibold text-lg font-mono-num text-primary">{formatIDR(p.package_price)}</div>
                  {p.price_overridden && <div className="text-[10px] text-muted-foreground">computed: {formatIDR(p.computed_total || 0)}</div>}
                </div>
                <div className="flex items-center gap-1">
                  {canEdit && <Button variant="ghost" size="icon" className="h-8 w-8" onClick={() => openEdit(p)} data-testid={`packages-edit-${p.id}`}><Pencil className="h-4 w-4" /></Button>}
                  {canDelete && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild><Button variant="ghost" size="icon" className="h-8 w-8 text-red-300" data-testid={`packages-delete-${p.id}`}><Trash2 className="h-4 w-4" /></Button></AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader><AlertDialogTitle>Hapus paket?</AlertDialogTitle><AlertDialogDescription>Penawaran/invoice yang sudah memakai paket ini tidak terpengaruh.</AlertDialogDescription></AlertDialogHeader>
                        <AlertDialogFooter><AlertDialogCancel>Batal</AlertDialogCancel><AlertDialogAction onClick={() => remove(p)}>Hapus</AlertDialogAction></AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-card border-border">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Paket' : 'Paket Baru'}</DialogTitle>
            <DialogDescription>Isi nama paket, pilih produk-produk yang termasuk.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Nama Paket *</Label>
                <Input data-testid="package-form-name" value={form.package_name} onChange={(e) => setForm({ ...form, package_name: e.target.value })} required />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Kategori</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATS.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="flex items-center gap-3 mt-7">
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} />
                <Label className="text-sm cursor-pointer">Aktif</Label>
              </div>
              <div className="space-y-1.5 md:col-span-2">
                <Label className="text-xs">Deskripsi</Label>
                <Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>

            {/* Package Items */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Item Paket</Label>
                <Button type="button" size="sm" variant="secondary" onClick={() => setProductPickOpen(true)} data-testid="package-add-products"><Plus className="h-3.5 w-3.5 mr-1" />Tambah Produk</Button>
              </div>
              <div className="rounded-lg border border-border overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/5 text-xs text-muted-foreground">
                    <tr>
                      <th className="px-3 py-2 text-left">Produk</th>
                      <th className="px-3 py-2 text-right w-20">Qty</th>
                      <th className="px-3 py-2 text-left w-24">Satuan</th>
                      <th className="px-3 py-2 text-right w-32">Harga</th>
                      <th className="px-3 py-2 text-right w-28">Diskon</th>
                      <th className="px-3 py-2 text-right w-32">Subtotal</th>
                      <th className="w-10"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.items.length === 0 && <tr><td colSpan={7} className="text-center py-6 text-muted-foreground">Belum ada item. Klik “Tambah Produk”.</td></tr>}
                    {form.items.map((it, idx) => {
                      const sub = Math.max(0, (parseFloat(it.quantity) || 0) * (parseFloat(it.unit_price) || 0) - (parseFloat(it.discount) || 0));
                      return (
                        <tr key={it.id || idx} className="border-t border-border">
                          <td className="px-2 py-1.5">
                            <div className="font-medium text-sm">{it.item_name}</div>
                            {it.description && <div className="text-xs text-muted-foreground">{it.description}</div>}
                          </td>
                          <td className="px-2 py-1.5"><NumberInput className="h-8 text-right font-mono-num" value={it.quantity} onChange={(v) => setForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, quantity: v } : x) }))} /></td>
                          <td className="px-2 py-1.5 text-xs text-muted-foreground">{it.unit}</td>
                          <td className="px-2 py-1.5"><NumberInput className="h-8 text-right font-mono-num" value={it.unit_price} onChange={(v) => setForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, unit_price: v } : x) }))} /></td>
                          <td className="px-2 py-1.5"><NumberInput className="h-8 text-right font-mono-num" value={it.discount} onChange={(v) => setForm(f => ({ ...f, items: f.items.map((x, i) => i === idx ? { ...x, discount: v } : x) }))} /></td>
                          <td className="px-3 py-1.5 text-right font-mono-num text-sm">{formatIDR(sub)}</td>
                          <td className="px-2 py-1.5 text-right"><Button type="button" size="icon" variant="ghost" className="h-7 w-7 text-red-300" onClick={() => setForm(f => ({ ...f, items: f.items.filter((_, i) => i !== idx) }))}><X className="h-3.5 w-3.5" /></Button></td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pt-2 border-t border-border">
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Total Otomatis (dari item)</span>
                  <span className="font-mono-num font-semibold">{formatIDR(computedTotal)}</span>
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={form.price_overridden} onCheckedChange={(v) => setForm({ ...form, price_overridden: v, package_price: v ? form.package_price : computedTotal })} />
                  <Label className="text-sm cursor-pointer">Override harga paket secara manual</Label>
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Harga Paket Final (Rp)</Label>
                <NumberInput testid="package-form-price" className="h-11 md:h-9 text-base md:text-sm text-right font-mono-num" value={form.price_overridden ? form.package_price : computedTotal} disabled={!form.price_overridden} onChange={(v) => setForm({ ...form, package_price: v })} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs">Catatan</Label>
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Batal</Button>
              <Button type="submit" disabled={saving} data-testid="package-form-save">{saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Simpan</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ProductSelectorDialog open={productPickOpen} onOpenChange={setProductPickOpen} onPicked={onProductsPicked} multi />

      {/* Bulk Edit Dialog */}
      <BulkEditDialog
        open={bulkEditOpen}
        onOpenChange={setBulkEditOpen}
        count={selected.size}
        itemLabel="paket"
        fields={[
          { key: 'category', label: 'Kategori', type: 'select', options: CATS },
          { key: 'is_active', label: 'Status Aktif', type: 'boolean' },
          { key: 'notes', label: 'Catatan', type: 'textarea', placeholder: 'Catatan internal…' },
        ]}
        onSubmit={(payload) => runBulk('update', payload)}
      />

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent data-testid="packages-bulk-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Hapus {selected.size} paket?</AlertDialogTitle>
            <AlertDialogDescription>
              Tindakan ini permanen. Penawaran/invoice yang sudah memakai paket tidak terpengaruh karena disimpan sebagai snapshot.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Batal</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-500 hover:bg-red-600"
              onClick={async () => { await runBulk('delete'); setBulkDeleteOpen(false); }}
              data-testid="packages-bulk-delete-confirm"
            >
              Hapus Permanen
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
